// SDS picture_request material-size gate: measure, upscale (aura-sr), and size-control the material before inject.

import { store } from '../../../core/store.js'
import { CONFIG } from '../../../core/config.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'
import { toJpeg } from './image-file.js'


const log = createLogger('add-to-design')

// SDS SDK addSDKImage ceiling: a very large PNG (e.g. 59MP / 6.3MB) makes the
// SDK's internal upload hang past its old 30s timeout. We therefore convert the
// aura-sr output to JPEG and, if its long edge exceeds this, downscale toward
// MATERIAL_TARGET_LONG_EDGE so the byte size stays SDK-friendly while still
// clearing picture_request. why → SDS material production upscale link.
const MATERIAL_MAX_LONG_EDGE = 6000
const MATERIAL_TARGET_LONG_EDGE = 5500

/**
 * Parse a SDS picture_request string ("5000 px * 3017 px") into pixel dims.
 * SDS stores the REQUIRED production material size per product in the base raw
 * data_json.picture_request (see docs/references/sds-sdk/sds-data-model.md §6).
 * Returns null when the field is absent or unparseable (gate then skips).
 *
 * @param {unknown} raw
 * @returns {{ w: number, h: number } | null}
 */
function parsePictureRequest(raw) {
  if (typeof raw !== 'string') return null
  const m = raw.match(/(\d+)\s*px\s*[*x×]\s*(\d+)\s*px/i)
  if (!m) return null
  const w = Number.parseInt(m[1], 10)
  const h = Number.parseInt(m[2], 10)
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null
  return { w, h }
}

/**
 * Measure an image's intrinsic pixel size. Prefers explicitly-carried
 * width/height; otherwise loads the image and reads naturalWidth/Height.
 *
 * @param {string} url
 * @param {{ width?: number, height?: number }} [hint]
 * @returns {Promise<{ w: number, h: number } | null>}
 */
async function measureImage(url, hint) {
  const hw = Number(hint?.width)
  const hh = Number(hint?.height)
  if (Number.isFinite(hw) && Number.isFinite(hh) && hw > 0 && hh > 0) {
    return { w: hw, h: hh }
  }
  return await new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
    img.onerror = () => resolve(null)
    img.src = url
  })
}

/**
 * Best-effort upscale toward the product's picture_request via the anon
 * creator-api upscale endpoint (fal aura-sr, 4x). Returns the upscaled image
 * URL on success, or null on any failure so the caller falls back to the
 * original. The endpoint is anonymous and accepts only our CDN / fal.media
 * sources (SSRF-allowlisted server-side). why → SDS material upscale link.
 *
 * @param {string} imageUrl
 * @returns {Promise<string | null>}
 */
async function upscaleTowardTarget(imageUrl) {
  try {
    // CONFIG.FRONTEND_API is the creator-api base (mms-creator) — the same host
    // the anon catalog uses. In local dev it is '' (Vite proxy → prod worker).
    const res = await fetch(`${CONFIG.FRONTEND_API}/api/customer/upscale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_url: imageUrl }),
      // aura-sr on a large original runs ~7-30s; allow generous headroom.
      signal: AbortSignal.timeout(120000),
    })
    if (!res.ok) {
      log.warn(`Upscale endpoint returned HTTP ${res.status} — using original`)
      return null
    }
    const result = await res.json()
    const url = result?.data?.url || result?.url
    return typeof url === 'string' && url.length > 0 ? url : null
  } catch (err) {
    log.warn('Upscale failed, using original material:', err?.message || err)
    return null
  }
}

/**
 * Enforce the product's picture_request material-size standard before inject.
 * Reads picture_request off the current product (transparently transferred from
 * catalog — see modules/design/index.js). Steps:
 *   1. Measure the ORIGINAL via the w=4000 full-original variant.
 *   2. If it already meets picture_request → inject the URL as-is.
 *   3. Otherwise call the anon upscale endpoint (fal aura-sr 4x) for a big URL.
 *   4. Fetch the upscaled image and convert to JPEG (quality 0.92), downscaling
 *      to ~MATERIAL_TARGET_LONG_EDGE when the long edge exceeds
 *      MATERIAL_MAX_LONG_EDGE so the SDS SDK upload stays under its size ceiling
 *      while still clearing picture_request. Hand back a ready JPEG File.
 * Advisory, never hard-block (bao): on any miss it still injects, with a warn.
 *
 * @param {string} imageUrl - the w=4000 original URL
 * @param {{ width?: number, height?: number }} [sizeHint]
 * @returns {Promise<{ url?: string, file?: File }>}
 */
export async function ensureMaterialSize(imageUrl, sizeHint) {
  const current = store.getState('design.currentProduct')
  const target = parsePictureRequest(current?.picture_request)
  if (!target) return { url: imageUrl } // no requirement known — inject as-is

  const measured = await measureImage(imageUrl, sizeHint)
  if (!measured) {
    log.warn('Could not measure material size; injecting without picture_request check')
    return { url: imageUrl }
  }

  if (measured.w >= target.w && measured.h >= target.h) {
    return { url: imageUrl } // already meets production standard
  }

  log.warn(
    `Material ${measured.w}x${measured.h} below picture_request ${target.w}x${target.h} — upscaling`
  )
  const upscaledUrl = await upscaleTowardTarget(imageUrl)
  if (!upscaledUrl) {
    log.warn(
      `[add-to-design] upscale unavailable; injecting under-size material ` +
        `(${measured.w}x${measured.h} < ${target.w}x${target.h})`
    )
    eventBus.emit(EVENTS.TOAST_SHOW, {
      message: 'Material resolution is low; production quality may be affected',
      type: 'warning',
    })
    return { url: imageUrl }
  }

  // Fetch the upscaled image (aura-sr → PNG on fal.media) and convert to a
  // size-controlled JPEG. We hand the caller a ready File rather than a URL so
  // the SDS SDK receives JPEG (it rejects PNG/large blobs) without a second
  // proxy hop.
  try {
    const res = await fetch(upscaledUrl, {
      mode: 'cors',
      headers: { Accept: 'image/png, image/jpeg, image/*' },
      signal: AbortSignal.timeout(120000),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const file = await toJpeg(blob, 'design-img-' + Date.now() + '.jpg', {
      // Cover the product's picture_request exactly — both dims stay >= required.
      coverWidth: target.w,
      coverHeight: target.h,
      quality: 0.92,
    })
    const after = await measureImage(URL.createObjectURL(file))
    if (after && (after.w < target.w || after.h < target.h)) {
      log.warn(
        `[add-to-design] material still below picture_request after upscale ` +
          `(${after.w}x${after.h} < ${target.w}x${target.h}) — production may reject`
      )
      eventBus.emit(EVENTS.TOAST_SHOW, {
        message: 'Material resolution is low; production quality may be affected',
        type: 'warning',
      })
    }
    return { file }
  } catch (err) {
    log.warn('Failed to process upscaled material, using original:', err?.message || err)
    return { url: imageUrl }
  }
}
